import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // HOW this session came to exist, which the app lock needs and cannot infer.
  // Both paths end in setUser(data), so a lock watching only `user` sees a
  // restored cookie and a freshly typed password as the same event -- and it
  // used to, which is why it never locked on a reopened browser.
  //   'fresh'    -- a password (and second factor) proved in THIS page load
  //   'restored' -- the browser presented a cookie; nothing was proved here
  const [sessionOrigin, setSessionOrigin] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
      // Never downgrade a session that was proved in this page load: checkAuth
      // is exported and called again elsewhere, and flipping to 'restored'
      // would lock a member out of the app they just signed into.
      setSessionOrigin((origin) => origin || 'restored');
    } catch (error) {
      setUser(false);
      setSessionOrigin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Keep the session alive. The access token only lives 15 minutes; without
  // this, every request after that silently 401s (notes fail to save, messages
  // fail to load, ...). On a 401 we transparently rotate the refresh token once
  // and replay the original request. Concurrent 401s share a single refresh.
  useEffect(() => {
    let refreshing = null;

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const { response, config } = error;
        if (!response || response.status !== 401 || !config || config._arcaRetried) {
          return Promise.reject(error);
        }
        const url = config.url || '';
        // Never try to refresh for the auth endpoints themselves — a 401 there
        // is a real answer (wrong password / no session), not an expired token.
        if (
          url.includes('/api/auth/refresh') ||
          url.includes('/api/auth/login') ||
          url.includes('/api/auth/2fa/verify')
        ) {
          return Promise.reject(error);
        }

        config._arcaRetried = true;
        try {
          if (!refreshing) {
            refreshing = axios
              .post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true })
              .finally(() => { refreshing = null; });
          }
          await refreshing;
        } catch (refreshError) {
          setUser(false); // refresh itself failed — the session is really gone
          setSessionOrigin(null);
          return Promise.reject(error);
        }
        return axios(config); // replay with the fresh cookie
      }
    );

    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // Accounts with 2FA don't get a session yet — a second step is required.
      if (data && data.two_factor_required) {
        return { success: false, twoFactorRequired: true };
      }
      // Staff without 2FA get no session either, and must set it up first.
      // Not a warning that can be dismissed: the account can read every
      // support thread, so a password on its own is not enough to hold it.
      if (data && data.enrollment_required) {
        return { success: false, enrollmentRequired: true };
      }
      setUser(data);
      setSessionOrigin('fresh');
      return { success: true };
    } catch (e) {
      // status lets the page word its own message without
      // pattern-matching server text; 0 means the server was never reached.
      return {
        success: false,
        status: e.response?.status || 0,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  };

  const verifyTwoFactor = async (code) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/2fa/verify`,
        { code },
        { withCredentials: true }
      );
      setUser(data);
      setSessionOrigin('fresh');
      return { success: true };
    } catch (e) {
      return {
        success: false,
        status: e.response?.status || 0,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Best-effort logout; cookies may already be invalid
    } finally {
      // Belt-and-suspenders: tell the service worker to wipe its caches.
      // The SW never caches sensitive content, but we clear on logout anyway.
      try {
        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHES' });
        }
      } catch (e) { /* ignore */ }
      setUser(false);
      setSessionOrigin(null);
    }
  };

  const value = {
    user,
    sessionOrigin,
    loading,
    login,
    verifyTwoFactor,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
